// frontend/src/components/Login.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import api, { enrollTrustedDevice, trustedDeviceAutoLogin } from "../api";
import socketService from "../services/socketService";
import sessionManager from "../services/sessionManager";
import toastService from '../services/toastService';
import { getBackendUrl, getTurnstileSiteKey } from "../config/apiConfig";
import logger from '../utils/logger';
import {
  clearAllTrustedDeviceTokens,
  clearTrustedDeviceToken,
  getTrustedDeviceContext,
  getTrustedDeviceToken,
  getTrustedUsernames,
  setTrustedDeviceToken,
} from '../utils/trustedDevice';
import {
  biometricLogin,
  clearCredential,
  isBiometricAvailable,
  isCredentialSaved,
  isNativePlatform,
  saveCredential,
} from '../services/biometricAuth';
import SEO from "./SEO";
import { getPageSEO } from "../utils/seo";
// Using Cloudflare Turnstile instead of reCAPTCHA (100% free, no limits)
import Turnstile from "react-turnstile";
import "./Login.css";

const getHomeRoute = (homePage) => {
  const homeRoutes = {
    dashboard: '/dashboardv2',
    search: '/search',
    messages: '/messages',
  };
  return homeRoutes[homePage] || '/dashboardv2';
};

const Login = () => {
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState(false);
  const [captchaRetryCount, setCaptchaRetryCount] = useState(0);
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const turnstileRef = useRef();
  const ssoExchangeGuardRef = useRef({ code: null });
  const navigate = useNavigate();
  const location = useLocation();
  const pageSEO = getPageSEO('login');
  const isDevelopment = process.env.NODE_ENV !== 'production';

  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricSaved, setBiometricSaved] = useState(false);
  const [enableBiometricOnDevice, setEnableBiometricOnDevice] = useState(false);
  const [pendingTrustedPrompt, setPendingTrustedPrompt] = useState(null);
  const [trustedPromptLoading, setTrustedPromptLoading] = useState(false);
  const [autoLoginStatus, setAutoLoginStatus] = useState('');
  const [autoLoginInProgress, setAutoLoginInProgress] = useState(false);
  const [trustedUsernames, setTrustedUsernames] = useState([]);
  const [selectedTrustedUsername, setSelectedTrustedUsername] = useState('');
  const [autoLoginDebug, setAutoLoginDebug] = useState({
    trustedUsernamesCount: 0,
    hasGenericToken: false,
    lastFailureStatus: null,
    lastAttemptSource: '',
  });
  const autoLoginStatusTimerRef = useRef(null);

  // MFA State
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaChannel, setMfaChannel] = useState("");
  const [contactMasked, setContactMasked] = useState("");
  const [showBackupCodeInput, setShowBackupCodeInput] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);

  // Hide topbar and remove body padding on login page
  useEffect(() => {
    const topbar = document.querySelector('.top-bar');
    if (topbar) {
      topbar.style.display = 'none';
    }
    
    // Remove any body padding/margin
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    
    // Show topbar and restore body styles when leaving login page
    return () => {
      const topbar = document.querySelector('.top-bar');
      if (topbar) {
        topbar.style.display = 'flex';
      }
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    const initBiometrics = async () => {
      if (!isNativePlatform()) return;
      const availability = await isBiometricAvailable();
      const supported = !!availability?.isAvailable;
      setBiometricSupported(supported);
      if (!supported) return;
      const saved = await isCredentialSaved();
      setBiometricSaved(saved);
    };
    initBiometrics();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ssoCode = params.get('sso_code');
    if (!ssoCode) return;

    if (ssoExchangeGuardRef.current.code === ssoCode) return;
    ssoExchangeGuardRef.current.code = ssoCode;

    const redirectParam = params.get('redirect');
    const safeRedirect = typeof redirectParam === 'string' && redirectParam.startsWith('/') ? redirectParam : null;

    const exchange = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.post(
          `${getBackendUrl()}/api/auth/sso/exchange`,
          { code: ssoCode },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const user = res.data?.user;
        const accessToken = res.data?.access_token;
        if (!user?.username || !accessToken) {
          throw new Error('Invalid SSO exchange response');
        }

        localStorage.setItem('username', user.username);
        localStorage.setItem('token', accessToken);

        if (res.data.refresh_token) {
          localStorage.setItem('refreshToken', res.data.refresh_token);
        }

        const userStatus = user.accountStatus || 'active';
        localStorage.setItem('userStatus', userStatus);

        const userRole = user.role_name || user.role || 'free_user';
        localStorage.setItem('userRole', userRole);

        sessionStorage.removeItem('photoReminderDismissed');
        sessionManager.init();
        socketService.connect(user.username);
        localStorage.removeItem('appTheme');

        window.dispatchEvent(new Event('loginStatusChanged'));
        window.dispatchEvent(new Event('userLoggedIn'));

        const homePage = user.homePage || localStorage.getItem('homePage') || 'dashboard';
        localStorage.setItem('homePage', homePage);

        navigate(safeRedirect || getHomeRoute(homePage), { replace: true, state: { user } });
      } catch (err) {
        logger.error('SSO exchange failed', err);
        setError('Single sign-on failed. Please log in.');
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    exchange();
  }, [location.search, navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const captureTrustedDebug = (extra = {}) => {
    if (!isDevelopment) return;
    const usernames = getTrustedUsernames();
    const genericToken = getTrustedDeviceToken();
    setAutoLoginDebug((prev) => ({
      ...prev,
      trustedUsernamesCount: usernames.length,
      hasGenericToken: Boolean(genericToken),
      ...extra,
    }));
  };

  const persistAuthState = (loginData) => {
    localStorage.setItem('username', loginData.user.username);
    localStorage.setItem('token', loginData.access_token);
    if (loginData.refresh_token) {
      localStorage.setItem('refreshToken', loginData.refresh_token);
    }

    const userStatus = loginData.user.accountStatus || 'active';
    localStorage.setItem('userStatus', userStatus);

    const userRole = loginData.user.role_name || loginData.user.role || 'free_user';
    localStorage.setItem('userRole', userRole);
  };

  const completeLoginFlow = async (loginData, options = {}) => {
    const { runUnattendedCheck = true } = options;

    persistAuthState(loginData);

    if (enableBiometricOnDevice) {
      try {
        if (loginData.refresh_token) {
          const saveRes = await saveCredential({
            username: loginData.user.username,
            refreshToken: loginData.refresh_token,
          });
          if (saveRes?.ok) {
            setBiometricSaved(true);
            toastService.success('Biometric login enabled on this device', 4000);
          } else if (saveRes?.error) {
            toastService.warning(saveRes.error, 5000);
          }
        } else {
          toastService.warning('Biometric login requires a refresh token. Please log in again.', 6000);
        }
      } catch (_) {
        toastService.warning('Could not enable biometric login on this device.', 6000);
      }
    }

    logger.debug('User role saved', { userRole: localStorage.getItem('userRole') });
    sessionStorage.removeItem('photoReminderDismissed');
    sessionManager.init();
    logger.debug('Session manager initialized');
    socketService.connect(loginData.user.username);
    localStorage.removeItem('appTheme');
    window.dispatchEvent(new Event('loginStatusChanged'));
    window.dispatchEvent(new Event('userLoggedIn'));

    if (loginData.mfa_warning) {
      sessionStorage.setItem('mfa_warning', JSON.stringify(loginData.mfa_warning));
    }

    const urlParams = new URLSearchParams(location.search);
    const requestedRedirect = urlParams.get('redirect');
    const safeRequestedRedirect = typeof requestedRedirect === 'string' && requestedRedirect.startsWith('/')
      ? requestedRedirect
      : null;

    if (!runUnattendedCheck) {
      const homePage = loginData.user.homePage || 'dashboard';
      localStorage.setItem('homePage', homePage);
      navigate(safeRequestedRedirect || getHomeRoute(homePage), { state: { user: loginData.user } });
      return;
    }

    let redirectPath = '/dashboardv2';
    try {
      logger.debug('Checking for unattended chats');
      const unattendedRes = await api.get('/messages/unattended');
      const unattendedData = unattendedRes.data;
      logger.debug('Unattended chats response', unattendedData);

      const hasWarnings = (unattendedData.warningCount || 0) > 0;
      const hasCritical = (unattendedData.criticalCount || 0) > 0;

      if (hasCritical) {
        logger.info('Critical unattended chats found; redirecting to messages', {
          criticalCount: unattendedData.criticalCount,
          warningCount: unattendedData.warningCount || 0,
        });
        redirectPath = '/messages';
        sessionStorage.setItem('unattendedChatsAlert', JSON.stringify({
          count: unattendedData.criticalCount,
          critical: unattendedData.criticalCount,
          warning: unattendedData.warningCount || 0,
        }));
      } else if (hasWarnings) {
        logger.info('Unattended chat warnings found', {
          warningCount: unattendedData.warningCount,
          highCount: unattendedData.highCount || 0,
          mediumCount: unattendedData.mediumCount || 0,
          pendingCount: unattendedData.pendingCount || 0,
        });
        sessionStorage.setItem('pendingMessagesWarning', JSON.stringify({
          count: unattendedData.warningCount,
          high: unattendedData.highCount || 0,
          medium: unattendedData.mediumCount || 0,
          pending: unattendedData.pendingCount || 0,
        }));

        if (safeRequestedRedirect) {
          redirectPath = safeRequestedRedirect;
        } else {
          const homePage = loginData.user.homePage || 'dashboard';
          localStorage.setItem('homePage', homePage);
          redirectPath = getHomeRoute(homePage);
        }
      } else if (safeRequestedRedirect) {
        redirectPath = safeRequestedRedirect;
      } else {
        const homePage = loginData.user.homePage || 'dashboard';
        localStorage.setItem('homePage', homePage);
        redirectPath = getHomeRoute(homePage);
      }
    } catch (unattendedErr) {
      logger.warn('Could not check unattended chats', unattendedErr);
      if (safeRequestedRedirect) {
        redirectPath = safeRequestedRedirect;
      } else {
        const homePage = loginData.user.homePage || 'dashboard';
        localStorage.setItem('homePage', homePage);
        redirectPath = getHomeRoute(homePage);
      }
    }

    navigate(redirectPath, { state: { user: loginData.user } });
  };

  const handleTrustedUserLogin = async (username) => {
    captureTrustedDebug({ lastAttemptSource: 'manual', lastFailureStatus: null });
    const token = getTrustedDeviceToken(username);
    if (!token) {
      setAutoLoginStatus('Selected account is no longer trusted. Please log in with password.');
      setTrustedUsernames((prev) => prev.filter((u) => u !== username));
      return;
    }

    setAutoLoginInProgress(true);
    setAutoLoginStatus(`Signing in as ${username}...`);

    try {
      const deviceContext = getTrustedDeviceContext();
      const autoLoginResponse = await trustedDeviceAutoLogin({
        trusted_device_token: token,
        device_id: deviceContext.deviceId,
        app_id: deviceContext.appId,
      });

      if (autoLoginResponse?.access_token && autoLoginResponse?.user?.username) {
        setAutoLoginStatus('Auto-login enabled. Logging you in...');
        await completeLoginFlow(autoLoginResponse, { runUnattendedCheck: true });
        return;
      }

      setAutoLoginStatus('Auto-login failed. Please log in with password.');
      setTrustedUsernames((prev) => prev.filter((u) => u !== username));
    } catch (autoLoginError) {
      logger.debug('Trusted-device auto-login failed on login page', autoLoginError);
      captureTrustedDebug({
        lastAttemptSource: 'manual',
        lastFailureStatus: autoLoginError?.response?.status || 'network',
      });
      if (autoLoginError?.response?.status === 401) {
        clearTrustedDeviceToken(token);
      }
      setAutoLoginStatus('Auto-login failed. Please log in with password.');
      setTrustedUsernames((prev) => prev.filter((u) => u !== username));
    } finally {
      setAutoLoginInProgress(false);
    }
  };

  const handleResetAutoLogin = () => {
    clearAllTrustedDeviceTokens();
    setTrustedUsernames([]);
    setSelectedTrustedUsername('');
    setAutoLoginStatus('');
    setAutoLoginInProgress(false);
  };

  useEffect(() => {
    let isActive = true;

    const clearStatusTimer = () => {
      if (autoLoginStatusTimerRef.current) {
        clearTimeout(autoLoginStatusTimerRef.current);
        autoLoginStatusTimerRef.current = null;
      }
    };

    const showTransientStatus = (message, durationMs = 2600) => {
      if (!isActive) return;
      clearStatusTimer();
      setAutoLoginStatus(message);
      autoLoginStatusTimerRef.current = setTimeout(() => {
        if (isActive) {
          setAutoLoginStatus('');
        }
      }, durationMs);
    };

    const attemptTrustedAutoLogin = async () => {
      captureTrustedDebug({ lastAttemptSource: 'startup', lastFailureStatus: null });
      const usernames = getTrustedUsernames();
      const legacyToken = getTrustedDeviceToken();

      // Legacy single-token fallback: if there is a generic token but no per-user map,
      // treat it as a single anonymous account and let the backend identify the user.
      const accounts = usernames.length > 0 ? usernames : (legacyToken ? [''] : []);

      if (accounts.length === 0) {
        if (isActive) {
          showTransientStatus('Auto-login not enabled on this device.');
          setAutoLoginInProgress(false);
        }
        return;
      }

      if (accounts.length > 1) {
        if (isActive) {
          setTrustedUsernames(accounts);
          setSelectedTrustedUsername(accounts[0]);
          setAutoLoginStatus('Multiple saved accounts found. Select one to sign in.');
          setAutoLoginInProgress(false);
        }
        return;
      }

      // Exactly one saved account -> attempt direct auto-login.
      const username = accounts[0];
      const token = username ? getTrustedDeviceToken(username) : legacyToken;
      if (isActive) {
        setAutoLoginInProgress(true);
        clearStatusTimer();
        setAutoLoginStatus('Checking auto-login...');
      }

      try {
        const deviceContext = getTrustedDeviceContext();
        const autoLoginResponse = await trustedDeviceAutoLogin({
          trusted_device_token: token,
          device_id: deviceContext.deviceId,
          app_id: deviceContext.appId,
        });

        if (autoLoginResponse?.access_token && autoLoginResponse?.user?.username) {
          if (isActive) {
            setAutoLoginStatus('Auto-login enabled. Logging you in...');
          }
          await completeLoginFlow(autoLoginResponse, { runUnattendedCheck: true });
          return;
        }
        showTransientStatus('Auto-login not enabled on this device.');
      } catch (autoLoginError) {
        logger.debug('Trusted-device auto-login failed on login page', autoLoginError);
        captureTrustedDebug({
          lastAttemptSource: 'startup',
          lastFailureStatus: autoLoginError?.response?.status || 'network',
        });
        if (autoLoginError?.response?.status === 401) {
          clearTrustedDeviceToken(token);
        }
        showTransientStatus('Auto-login not enabled on this device.');
      } finally {
        if (isActive) {
          setAutoLoginInProgress(false);
        }
      }
    };

    attemptTrustedAutoLogin();

    return () => {
      isActive = false;
      clearStatusTimer();
    };
  }, []);

  const handleTrustedPromptDecision = async (shouldTrust) => {
    if (!pendingTrustedPrompt) return;

    const loginData = pendingTrustedPrompt;
    setTrustedPromptLoading(true);
    setError('');
    try {
      if (shouldTrust) {
        persistAuthState(loginData);
        const deviceContext = getTrustedDeviceContext();
        const enrollResponse = await enrollTrustedDevice({
          device_id: deviceContext.deviceId,
          device_name: deviceContext.deviceName,
          platform: deviceContext.platform,
          app_id: deviceContext.appId,
        });
        if (enrollResponse?.trusted_device_token) {
          setTrustedDeviceToken(loginData.user?.username, enrollResponse.trusted_device_token);
          toastService.success('Device trusted for 30 days', 4000);
        }
      }

      setPendingTrustedPrompt(null);
      await completeLoginFlow(loginData, { runUnattendedCheck: true });
    } catch (trustError) {
      logger.error('Trusted device enrollment failed', trustError);
      toastService.warning('Could not save this device right now. Continuing with normal login.', 5000);
      setPendingTrustedPrompt(null);
      await completeLoginFlow(loginData, { runUnattendedCheck: true });
    } finally {
      setTrustedPromptLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    // Verify CAPTCHA (allow bypass if Cloudflare is having issues after 3 retries)
    if (!isDevelopment && !captchaToken && !canBypassCaptcha) {
      setError("Please complete the CAPTCHA verification");
      setLoading(false);
      return;
    }
    
    try {
      // Trim whitespace from credentials
      const username = form.username.trim();
      const credentials = {
        username,
        password: form.password.trim(),
        captchaToken: isDevelopment ? "XXXX.DUMMY.TOKEN.XXXX" : captchaToken,
        device_id: getTrustedDeviceContext().deviceId,
        app_id: getTrustedDeviceContext().appId,
        trusted_device_token: getTrustedDeviceToken(username) || undefined,
      };
      const res = await api.post("/login", credentials);

      if (res.data.show_trusted_device_prompt) {
        setPendingTrustedPrompt(res.data);
        return;
      }

      await completeLoginFlow(res.data, { runUnattendedCheck: true });
    } catch (err) {
      logger.error('Login error', err);
      
      // Reset CAPTCHA on error
      if (turnstileRef.current) {
        turnstileRef.current.reset();
        setCaptchaToken(null);
      }
      
      // Check if MFA is required
      if (err.response?.status === 403 && err.response?.data?.detail === "MFA_REQUIRED") {
        setMfaRequired(true);
        setMfaChannel(err.response.data.mfa_channel || "email");
        setContactMasked(err.response.data.contact_masked || "");
        setError("");
        
        // Send MFA code automatically
        try {
          await sendMfaCode();
        } catch (mfaErr) {
          logger.error('Failed to send MFA code', mfaErr);
          // Error message is already set by sendMfaCode function
        }
      } else {
        setError(err.response?.data?.detail || err.response?.data?.error || "Invalid credentials");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaChange = (token) => {
    logger.debug('CAPTCHA verified', { ok: Boolean(token) });
    setCaptchaToken(token);
    setCaptchaError(false);
    setCaptchaLoaded(true);
    setError(""); // Clear error when CAPTCHA is completed
  };

  const handleCaptchaError = () => {
    logger.error('CAPTCHA error - Cloudflare Turnstile failed to load');
    setCaptchaError(true);
    setCaptchaRetryCount(prev => prev + 1);
    setCaptchaToken(null);
    setCaptchaLoaded(false);
  };

  const handleCaptchaExpire = () => {
    logger.debug('CAPTCHA expired - resetting');
    setCaptchaToken(null);
  };

  const retryCaptcha = () => {
    setCaptchaError(false);
    setCaptchaLoaded(false);
    setCaptchaToken(null);
    if (turnstileRef.current) {
      turnstileRef.current.reset();
    }
  };

  useEffect(() => {
    if (mfaRequired || captchaLoaded || captchaError || captchaToken) return;
    const timer = setTimeout(() => {
      setCaptchaError(true);
      setCaptchaRetryCount(prev => prev + 1);
    }, 12000);
    return () => clearTimeout(timer);
  }, [mfaRequired, captchaLoaded, captchaError, captchaToken]);

  // Allow bypass after 3 failed captcha attempts (Cloudflare issues)
  const canBypassCaptcha = captchaRetryCount >= 3;

  const sendMfaCode = async () => {
    try {
      setResendingCode(true);
      // Use axios directly to avoid double-prefixing
      const response = await axios.post(
        `${getBackendUrl()}/api/auth/mfa/send-code`,
        { username: form.username.trim() }
      );
      
      if (response.data.mock_code) {
        setError(`DEV MODE: Use code ${response.data.mock_code}`);
      }
    } catch (err) {
      logger.error('Error sending MFA code', err);
      // Show the actual backend error message
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || "Failed to send verification code";
      setError(errorMessage);
    } finally {
      setResendingCode(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      // Login with MFA code - use the same /login endpoint with mfa_code
      const username = form.username.trim();
      const credentials = {
        username,
        password: form.password.trim(),
        mfa_code: mfaCode.trim(),
        captchaToken: isDevelopment ? "XXXX.DUMMY.TOKEN.XXXX" : captchaToken,
        device_id: getTrustedDeviceContext().deviceId,
        app_id: getTrustedDeviceContext().appId,
        trusted_device_token: getTrustedDeviceToken(username) || undefined,
      };
      const res = await api.post("/login", credentials);

      if (res.data.show_trusted_device_prompt) {
        setPendingTrustedPrompt(res.data);
        return;
      }

      await completeLoginFlow(res.data, { runUnattendedCheck: true });
    } catch (err) {
      logger.error('MFA verification error', err);
      setError(err.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaRequired(false);
    setMfaCode("");
    setError("");
    setShowBackupCodeInput(false);
  };

  const handleBiometricLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const redirectParam = new URLSearchParams(location.search).get('redirect');
      const safeRedirect = typeof redirectParam === 'string' && redirectParam.startsWith('/') ? redirectParam : null;

      const res = await biometricLogin();
      if (!res?.ok) {
        setError(res?.error || 'Biometric login failed.');
        return;
      }

      const user = res.user || {};
      const username = user.username || res.username;
      if (!username) {
        setError('Biometric login failed. Please log in with password.');
        return;
      }

      localStorage.setItem('username', username);
      localStorage.setItem('token', res.accessToken);
      if (res.refreshToken) {
        localStorage.setItem('refreshToken', res.refreshToken);
      }

      const userStatus = user.accountStatus || 'active';
      localStorage.setItem('userStatus', userStatus);

      const userRole = user.role_name || user.role || 'free_user';
      localStorage.setItem('userRole', userRole);

      sessionStorage.removeItem('photoReminderDismissed');
      sessionManager.init();
      socketService.connect(username);
      localStorage.removeItem('appTheme');

      window.dispatchEvent(new Event('loginStatusChanged'));
      window.dispatchEvent(new Event('userLoggedIn'));

      const homePage = user.homePage || localStorage.getItem('homePage') || 'dashboard';
      localStorage.setItem('homePage', homePage);

      navigate(safeRedirect || getHomeRoute(homePage), { replace: true, state: { user } });
    } catch (e) {
      setError(e?.message || 'Biometric login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearBiometric = async () => {
    try {
      await clearCredential();
      setBiometricSaved(false);
      setEnableBiometricOnDevice(false);
      toastService.info('Biometric login removed from this device', 4000);
    } catch (_) {
      toastService.warning('Could not remove biometric login from this device.', 5000);
    }
  };

  return (
    <>
      <SEO
        title={pageSEO.title}
        description={pageSEO.description}
        keywords={pageSEO.keywords}
        url={pageSEO.url}
        noindex={pageSEO.noindex}
      />
    <div className="login-page-wrapper" style={{
      background: `
        linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
        url('/images/wedding-bg.jpg') center/cover no-repeat fixed
      `
    }}>
      {/* Dark overlay for better readability */}
      <div className="login-page-overlay"></div>
      
      <div className="login-container">
      {autoLoginStatus && !pendingTrustedPrompt && !mfaRequired && (
        <div className="trusted-autologin-status" role="status" aria-live="polite">
          <span className={`trusted-autologin-dot${autoLoginInProgress ? ' loading' : ''}`} aria-hidden="true">🦋</span>
          <span>{autoLoginStatus}</span>
        </div>
      )}
      {isDevelopment && !pendingTrustedPrompt && !mfaRequired && (
        <div className="autologin-debug" role="note" aria-label="Auto-login debug">
          <div>Auto-login debug</div>
          <div>Saved usernames: {autoLoginDebug.trustedUsernamesCount}</div>
          <div>Generic token present: {autoLoginDebug.hasGenericToken ? 'yes' : 'no'}</div>
          <div>Last source: {autoLoginDebug.lastAttemptSource || 'n/a'}</div>
          <div>Last failure status: {autoLoginDebug.lastFailureStatus ?? 'none'}</div>
        </div>
      )}
      {trustedUsernames.length > 1 && !pendingTrustedPrompt && !mfaRequired && (
        <div className="mb-3">
          <label className="login-form-label">Choose a saved account</label>
          <select
            className="login-input"
            value={selectedTrustedUsername}
            onChange={(e) => setSelectedTrustedUsername(e.target.value)}
            disabled={autoLoginInProgress}
          >
            {trustedUsernames.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
          <button
            type="button"
            className="login-button w-100 mt-3"
            onClick={() => handleTrustedUserLogin(selectedTrustedUsername)}
            disabled={autoLoginInProgress || !selectedTrustedUsername}
          >
            {autoLoginInProgress ? 'Signing in...' : `Sign in as ${selectedTrustedUsername}`}
          </button>
          <button
            type="button"
            className="login-button login-biometric-clear w-100 mt-3"
            onClick={handleResetAutoLogin}
            disabled={autoLoginInProgress}
          >
            Use a different account
          </button>
        </div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}
      {pendingTrustedPrompt && (
        <div className="trusted-device-prompt">
          <h3 className="trusted-device-title">Save this device for 30 days?</h3>
          <p className="trusted-device-text">
            Next time, you can sign in on this device without entering username and password.
          </p>
          <div className="trusted-device-actions">
            <button
              type="button"
              className="login-button trusted-device-yes"
              onClick={() => handleTrustedPromptDecision(true)}
              disabled={trustedPromptLoading}
            >
              {trustedPromptLoading ? 'Saving...' : 'Yes, trust this device'}
            </button>
            <button
              type="button"
              className="trusted-device-no"
              onClick={() => handleTrustedPromptDecision(false)}
              disabled={trustedPromptLoading}
            >
              No, continue login
            </button>
          </div>
        </div>
      )}
      
      {!mfaRequired ? (
        /* Regular Login Form */
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="login-form-label">Username</label>
            <div className="login-input-wrapper">
              {!form.username && (
                <span className="login-input-icon">👤</span>
              )}
              <input
                type="text"
                name="username"
                className="login-input"
                value={form.username}
                onChange={handleChange}
                required
                placeholder="Enter your username"
              />
            </div>
          </div>
          
          <div className="mb-3">
            <label className="login-form-label">Password</label>
            <div className="login-input-wrapper">
              {!form.password && (
                <span className="login-input-icon">🔒</span>
              )}
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="login-input"
                value={form.password}
                onChange={handleChange}
                required
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="login-password-toggle"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <Link to="/forgot-password" className="login-forgot-link">Forgot Password?</Link>
          </div>
          
          <div className="login-captcha-container">
            {captchaError ? (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '12px 16px',
                textAlign: 'center',
                width: '100%',
                maxWidth: '300px'
              }}>
                <p style={{ color: '#ef4444', fontSize: '13px', margin: '0 0 8px 0' }}>
                  ⚠️ Security check unavailable
                </p>
                {captchaRetryCount < 3 ? (
                  <button
                    type="button"
                    onClick={retryCaptcha}
                    style={{
                      background: 'transparent',
                      border: '1px solid #ef4444',
                      color: '#ef4444',
                      padding: '6px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Retry ({3 - captchaRetryCount} attempts left)
                  </button>
                ) : (
                  <p style={{ color: '#6b7280', fontSize: '12px', margin: '0' }}>
                    ✓ Cloudflare issue detected - proceeding without captcha
                  </p>
                )}
              </div>
              ) : (
                <Turnstile
                  ref={turnstileRef}
                  sitekey={getTurnstileSiteKey()}
                  onVerify={handleCaptchaChange}
                  onLoad={() => setCaptchaLoaded(true)}
                  onError={handleCaptchaError}
                  onExpire={handleCaptchaExpire}
                  theme="light"
                />
              )}
          </div>

          {biometricSupported && (
            <div className={`login-biometric-row${biometricSaved ? ' with-button' : ''}`}>
              <label className="login-biometric-toggle">
                <input
                  type="checkbox"
                  checked={enableBiometricOnDevice}
                  onChange={(e) => setEnableBiometricOnDevice(e.target.checked)}
                  aria-label="Enable biometric login on this device"
                />
                <span className="login-biometric-toggle-text-full">Enable biometric login on this device</span>
                <span className="login-biometric-toggle-text-short">Enable 👆</span>
              </label>

              {biometricSaved && (
                <button
                  type="button"
                  className="login-button login-biometric-btn"
                  onClick={handleBiometricLogin}
                  disabled={loading}
                  aria-label="Sign in with biometrics"
                >
                  {loading ? (
                    <>
                      <span className="login-biometric-btn-text-full">Authenticating...</span>
                      <span className="login-biometric-btn-text-short">...</span>
                    </>
                  ) : (
                    <>
                      <span className="login-biometric-btn-text-full">Sign In with Biometrics</span>
                      <span className="login-biometric-btn-text-short">👆 Bio</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {biometricSupported && biometricSaved && (
            <button
              type="button"
              className="login-biometric-clear"
              onClick={handleClearBiometric}
              disabled={loading}
            >
              Remove biometric login from this device
            </button>
          )}
          
          <button 
            type="submit" 
            disabled={loading || autoLoginInProgress || (!isDevelopment && !captchaToken && !canBypassCaptcha)}
            className="login-button"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
      </form>
      ) : (
        /* MFA Verification Form */
        <form onSubmit={handleMfaVerify}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
            padding: '16px',
            borderRadius: '12px',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14px', color: '#374151', margin: '0' }}>
              🔒 Code sent to: <strong>{contactMasked}</strong>
            </p>
          </div>

          <div className="mb-3">
            <label className="form-label" style={{
              fontWeight: '500',
              fontSize: '14px',
              color: '#374151',
              marginBottom: '8px',
              display: 'block',
              textAlign: 'center'
            }}>
              {showBackupCodeInput ? 'Backup Code (XXXX-XXXX)' : 'Verification Code'}
            </label>
            <input
              type="text"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(showBackupCodeInput ? '' : /\D/g, ''))}
              placeholder={showBackupCodeInput ? "XXXX-XXXX" : "000000"}
              maxLength={showBackupCodeInput ? 9 : 6}
              style={{
                width: '100%',
                padding: '16px',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: showBackupCodeInput ? '18px' : '32px',
                outline: 'none',
                textAlign: 'center',
                letterSpacing: showBackupCodeInput ? '0.2em' : '0.5em',
                fontFamily: 'monospace',
                backgroundColor: '#f9fafb',
                color: '#1f2937',
                fontWeight: '600'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              required
              autoFocus
            />
          </div>

          <button 
            type="submit" 
            disabled={loading || (!showBackupCodeInput && mfaCode.length !== 6) || (showBackupCodeInput && mfaCode.length !== 9)}
            style={{
              width: '100%',
              minHeight: '52px',
              fontSize: '16px',
              fontWeight: '600',
              padding: '14px',
              background: loading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.4)',
              marginBottom: '12px'
            }}
            onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-1px)', e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)')}
            onMouseLeave={(e) => !loading && (e.target.style.transform = 'translateY(0)', e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)')}
          >
            {loading ? "Verifying..." : "Verify & Sign In"}
          </button>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button 
              type="button"
              onClick={sendMfaCode}
              disabled={resendingCode}
              style={{
                flex: 1,
                padding: '12px',
                background: 'transparent',
                color: '#667eea',
                border: '2px solid #667eea',
                borderRadius: '12px',
                cursor: resendingCode ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {resendingCode ? 'Sending...' : 'Resend Code'}
            </button>
            <button 
              type="button"
              onClick={() => setShowBackupCodeInput(!showBackupCodeInput)}
              style={{
                flex: 1,
                padding: '12px',
                background: 'transparent',
                color: '#6b7280',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {showBackupCodeInput ? 'Use Code' : 'Use Backup'}
            </button>
          </div>

          <button 
            type="button"
            onClick={handleBackToLogin}
            style={{
              width: '100%',
              padding: '12px',
              background: 'transparent',
              color: '#6b7280',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              textDecoration: 'underline'
            }}
          >
            ← Back to Login
          </button>
        </form>
      )}
      
      {!mfaRequired && (
        <div className="login-register-prompt">
          <p>
            Don't have an account?{' '}
            <Link to="/register-interest">Register</Link>
          </p>
        </div>
      )}
      </div>
    </div>
    </>
  );
};

export default Login;
